import { useCallback, useEffect, useMemo, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import IconButton from "../../../Buttons/IconButton";
import CardMedia from "../../../Media/CardMedia";
import Loader from "../../../Loaders/Loader";
import { useTranslationApp } from "../../../../lib/i18next";
import { MODAL_COMPETITORS_AD_LIBRARY_CTA_LABEL } from "../../../../i18n/keysTranslations";
import { PRIMARY_COLOR_TEXT } from "../../../../utils/colors";
import {
  buildCompetitorAdMediaVersions,
  formatCompetitorAdCtaLabel,
} from "../utils";

const NOOP = () => {};

const getVersionAspectRatio = (version) => {
  const width = Number(version?.width);
  const height = Number(version?.height);

  if (
    Number.isFinite(width) &&
    width > 0 &&
    Number.isFinite(height) &&
    height > 0
  ) {
    return `${width} / ${height}`;
  }

  return "4 / 5";
};

const CarouselSlideLoader = () => (
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      zIndex: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#ffffff",
      pointerEvents: "none",
    }}
  >
    <Loader size={32} colorCustom={PRIMARY_COLOR_TEXT} hasMarginTop={false} />
  </Box>
);

const ThreeDCarouselWrapper = ({ isCarousel, stackUrls = [], children }) => {
  if (!isCarousel) return children;

  const visibleStack = (stackUrls || []).filter(Boolean).slice(0, 1);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
      }}
    >
      {visibleStack
        .slice()
        .reverse()
        .map((url, idx) => {
          const depth = idx + 1;
          const translateY = depth * 6;

          return (
            <Box
              key={`${url}-${idx}`}
              sx={{
                position: "absolute",
                inset: 0,
                overflow: "hidden",
                transform: `translateY(${translateY}px)`,
                opacity: 0.4 - idx * 0.1,
                border: "1px solid rgba(158, 158, 158, 0.7)",
                backgroundImage: `url(${url})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                boxShadow: "0 10px 30px rgba(0,0,0,0.28)",
                pointerEvents: "none",
              }}
            />
          );
        })}

      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const CarouselControls = ({
  isCarousel,
  totalImages,
  currentIndex,
  onPrev,
  onNext,
}) => {
  if (!isCarousel || totalImages <= 1) return null;

  const isFirstImage = currentIndex === 0;
  const isLastImage = currentIndex === totalImages - 1;

  return (
    <Box className="competitor-ad-library-carousel-controls">
      <IconButton
        onClick={onPrev}
        disabled={isFirstImage}
        sx={{
          position: "absolute",
          left: 7,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 3,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          color: "white",
          width: 36,
          height: 36,
          opacity: isFirstImage ? 0.8 : 1,
          "&:hover": {
            backgroundColor: isFirstImage
              ? "rgba(0, 0, 0, 0.6)"
              : "rgba(0, 0, 0, 0.8)",
          },
          "&.Mui-disabled": {
            opacity: 0.8,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            cursor: "not-allowed",
          },
          "& .MuiSvgIcon-root": {
            fontSize: 20,
          },
        }}
        type="icon"
      >
        <ChevronLeftIcon sx={{ color: "#fff !important" }} />
      </IconButton>
      <IconButton
        onClick={onNext}
        disabled={isLastImage}
        sx={{
          position: "absolute",
          right: 17,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 3,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          color: "white",
          width: 36,
          height: 36,
          opacity: isLastImage ? 0.8 : 1,
          cursor: isLastImage ? "not-allowed" : "pointer",
          "&:hover": {
            backgroundColor: isLastImage
              ? "rgba(0, 0, 0, 0.6)"
              : "rgba(0, 0, 0, 0.8)",
          },
          "&.Mui-disabled": {
            opacity: 0.8,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            cursor: "not-allowed",
          },
          "& .MuiSvgIcon-root": {
            fontSize: 20,
          },
        }}
        type="icon"
      >
        <ChevronRightIcon sx={{ color: "#fff !important" }} />
      </IconButton>
    </Box>
  );
};

const VideoMuteToggle = ({ isMuted, onToggleMute }) => (
  <Box
    className="gallery-video-mute-toggle"
    sx={{
      position: "absolute",
      inset: 0,
      zIndex: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: 0,
      transition: "opacity 0.2s ease",
      pointerEvents: "none",
    }}
  >
    <Box
      sx={{ pointerEvents: "auto", display: "inline-flex" }}
      onClick={onToggleMute}
    >
      <IconButton
        onClick={onToggleMute}
        aria-pressed={!isMuted}
        type="icon"
        sx={{
          pointerEvents: "auto",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          color: "white",
          width: 50,
          height: 50,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
          },
          "& .MuiSvgIcon-root": {
            fontSize: 30,
          },
        }}
      >
        {isMuted ? (
          <VolumeOffIcon sx={{ color: "#fff !important" }} />
        ) : (
          <VolumeUpIcon sx={{ color: "#fff !important" }} />
        )}
      </IconButton>
    </Box>
  </Box>
);

const CompetitorAdLibraryCard = ({
  item,
  isVideoMuted = true,
  onToggleVideoMute = NOOP,
  onActiveVersionChange = NOOP,
}) => {
  const { t } = useTranslationApp();
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [readyCarouselSlideUrl, setReadyCarouselSlideUrl] = useState(null);

  const mediaVersions = useMemo(
    () => buildCompetitorAdMediaVersions({ item }),
    [item]
  );

  const isCarousel = mediaVersions.length > 1;
  const activeVersion =
    mediaVersions[currentCarouselIndex] || mediaVersions[0] || null;
  const activeUrl = activeVersion?.url || item?.url;
  const activeAspectRatio = getVersionAspectRatio(
    mediaVersions[0] || activeVersion
  );
  const description = item?.description?.trim() || "";
  const headline = item?.headline?.trim() || "";
  const headlineSecondary = item?.headlineSecondary?.trim() || "";
  const ctaLabel = formatCompetitorAdCtaLabel(item?.cta);
  const showHeadlineCtaRow = Boolean(headline || headlineSecondary || ctaLabel);
  const isActiveVideo = activeVersion?.type === "video";
  const isCurrentCarouselSlideLoading =
    isCarousel && !!activeUrl && readyCarouselSlideUrl !== activeUrl;
  const carouselStackUrls = isCarousel
    ? mediaVersions
        .map((version) => version?.url)
        .filter((url) => !!url && url !== activeUrl)
    : [];

  const onCarouselPrev = useCallback((event) => {
    event?.stopPropagation();
    setCurrentCarouselIndex((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const onCarouselNext = useCallback(
    (event) => {
      event?.stopPropagation();
      const maxIndex = mediaVersions.length - 1;
      setCurrentCarouselIndex((prev) => (prev < maxIndex ? prev + 1 : prev));
    },
    [mediaVersions.length]
  );

  const onCarouselMediaLoad = useCallback(() => {
    if (!activeUrl) return;
    setReadyCarouselSlideUrl(activeUrl);
  }, [activeUrl]);

  const onCarouselMediaFailed = useCallback(() => {
    if (!activeUrl) return;
    setReadyCarouselSlideUrl(activeUrl);
  }, [activeUrl]);

  useEffect(() => {
    setCurrentCarouselIndex(0);
    setReadyCarouselSlideUrl(null);
  }, [item?.id, item?.url]);

  useEffect(() => {
    if (!activeVersion?.url) return;

    onActiveVersionChange({
      url: activeVersion.url,
      type: activeVersion.type === "video" ? "video" : "image",
    });
  }, [activeVersion, onActiveVersionChange]);

  if (!activeUrl) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#ffffff",
      }}
    >
      {description ? (
        <Typography
          component="p"
          sx={{
            px: 1.5,
            py: 1,
            m: 0,
            fontSize: 12,
            lineHeight: 1.5,
            color: "text.secondary",
            wordBreak: "break-word",
            borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
          }}
        >
          {description}
        </Typography>
      ) : null}

      <Box
        onContextMenu={(event) => event.preventDefault()}
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: activeAspectRatio,
          backgroundColor: isCarousel ? "#ffffff" : "#f3f3f3",
          overflow: "hidden",
          flexShrink: 0,
          "& img, & video": {
            WebkitUserDrag: "none",
            userSelect: "none",
            WebkitTouchCallout: "none",
          },
          "&:hover .gallery-video-mute-toggle": {
            opacity: isActiveVideo ? 1 : 0,
          },
          "&:hover .gallery-video-mute-toggle .MuiIconButton-root": {
            pointerEvents: isActiveVideo ? "auto" : "none",
          },
        }}
      >
        <ThreeDCarouselWrapper
          isCarousel={isCarousel}
          stackUrls={carouselStackUrls}
        >
          <CardMedia
            url={activeUrl}
            mediaType={activeVersion?.type === "video" ? "video" : "image"}
            onFailedLoadUrl={isCarousel ? onCarouselMediaFailed : NOOP}
            onMediaLoad={isCarousel ? onCarouselMediaLoad : undefined}
            sx={{
              objectFit: isCarousel ? "cover" : "contain",
              borderRadius: 0,
              width: "100%",
              height: "100%",
              opacity: isCurrentCarouselSlideLoading ? 0 : 1,
              transition: isCarousel ? "opacity 0.2s ease" : "none",
            }}
            sxVideo={{
              borderRadius: 0,
            }}
            propsType={{
              controls: false,
              autoplay: true,
              muted: isVideoMuted,
              loop: true,
              playsInline: true,
              draggable: false,
              controlsList: "nodownload",
            }}
          />
        </ThreeDCarouselWrapper>

        {isCurrentCarouselSlideLoading ? <CarouselSlideLoader /> : null}

        {isActiveVideo ? (
          <VideoMuteToggle
            isMuted={isVideoMuted}
            onToggleMute={onToggleVideoMute}
          />
        ) : null}

        <CarouselControls
          isCarousel={isCarousel}
          totalImages={mediaVersions.length}
          currentIndex={currentCarouselIndex}
          onPrev={onCarouselPrev}
          onNext={onCarouselNext}
        />
      </Box>

      {showHeadlineCtaRow ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 0.25,
            px: 1.5,
            py: 1,
            borderTop: "1px solid rgba(0, 0, 0, 0.08)",
          }}
        >
          {headline ? (
            <Typography
              sx={{
                fontSize: 12,
                lineHeight: 1.5,
                color: "text.secondary",
                wordBreak: "break-word",
              }}
            >
              {headline}
            </Typography>
          ) : null}
          {headlineSecondary ? (
            <Typography
              sx={{
                fontSize: 12,
                lineHeight: 1.5,
                color: "text.secondary",
                wordBreak: "break-word",
              }}
            >
              {headlineSecondary}
            </Typography>
          ) : null}
          {ctaLabel ? (
            <Typography
              sx={{
                fontSize: 12,
                lineHeight: 1.5,
                color: "text.secondary",
                wordBreak: "break-word",
                ...(headline || headlineSecondary ? { mt: 0.5 } : {}),
              }}
            >
              {t(MODAL_COMPETITORS_AD_LIBRARY_CTA_LABEL, { label: ctaLabel })}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export default CompetitorAdLibraryCard;
